import type {
  ColDef,
  EditableCallbackParams,
  ICellEditorParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import { isEqual } from 'lodash';

import { FreeTextDropdownEditor, type FreeTextDropdownEditorConfig } from './free-text-dropdown-editor';
import type { SampleSheetRowDraft } from './sample-sheet-data';
import { findUniqueSubstringMatch, normalizeAndTrim } from './dropdown-matching';

export type CellIssueLookup = (
  rowId: string,
  field: keyof SampleSheetRowDraft,
) => readonly string[] | null | undefined;

export type SnapMode = 'exact-only' | 'unique-substring';

export type CanonicalMatchMode = 'case-insensitive' | 'case-sensitive';

export type DropdownPolicy = {
  options: () => readonly string[];
  formatOption?: (value: string, row?: SampleSheetRowDraft) => string;
  snapMode: SnapMode;
  emptyLabel?: string;
  onCanonicalSelect?: (row: SampleSheetRowDraft, canonical: string) => SampleSheetRowDraft;
  onRawValue?: (row: SampleSheetRowDraft, value: string | null) => SampleSheetRowDraft;
  optionsForRow?: (row: SampleSheetRowDraft) => readonly string[];
  canonicalMatchMode?: CanonicalMatchMode;
};

export type DropdownRowMutation = {
  field: keyof SampleSheetRowDraft;
  previousValue: unknown;
  newValue: unknown;
};

export type DropdownSnapOptions = {
  suppressCanonicalSelect?: boolean;
};

const emptyString = '';


type DropdownRuntime = {
  getOptionsForRow: (row?: SampleSheetRowDraft) => readonly string[];
  getLabel: (candidate: string, row?: SampleSheetRowDraft) => string;
  commitValue: (raw: string, row?: SampleSheetRowDraft) => {
    value: string;
    canonical: string | null;
    options: readonly string[];
  };
  snapDropdownValue: (row: SampleSheetRowDraft, raw: string, options?: DropdownSnapOptions) => string;
  resolveCanonicalValue: (value: string, row?: SampleSheetRowDraft) => string | null;
};

type DropdownRowMutationHandler = (row: SampleSheetRowDraft, changes: DropdownRowMutation[]) => void;

function createDropdownRuntime(policy: DropdownPolicy, onRowMutation?: DropdownRowMutationHandler): DropdownRuntime {
  const getLabel = (candidate: string, row?: SampleSheetRowDraft) => {
    const label = policy.formatOption ? policy.formatOption(candidate, row) : candidate;
    return label && label.trim().length > 0 ? label : candidate;
  };

  const canonicalMatchMode: CanonicalMatchMode = policy.canonicalMatchMode ?? 'case-insensitive';

  const canonicalEquals = (candidate: string, normalized: string): boolean => {
    if (canonicalMatchMode === 'case-sensitive') {
      return candidate === normalized;
    }
    return candidate.toLowerCase() === normalized.toLowerCase();
  };

  const findCanonicalMatch = (
    value: string,
    options: readonly string[],
    row?: SampleSheetRowDraft,
  ): string | null => {
    const normalized = normalizeAndTrim(value);
    if (normalized === emptyString) {
      const emptyCandidate = options.find(candidate => normalizeAndTrim(candidate) === emptyString);
      return emptyCandidate ?? null;
    }
    for (const candidate of options) {
      const candidateNormalized = normalizeAndTrim(candidate);
      if (candidateNormalized === emptyString) {
        continue;
      }
      if (canonicalEquals(candidateNormalized, normalized)) {
        return candidate;
      }
      if (policy.formatOption) {
        const label = getLabel(candidate, row);
        const labelNormalized = normalizeAndTrim(label);
        if (labelNormalized && canonicalEquals(labelNormalized, normalized)) {
          return candidate;
        }
      }
    }
    return null;
  };

  const applyRowMutationResult = (row: SampleSheetRowDraft, updatedRow: SampleSheetRowDraft | null | undefined) => {
    if (!updatedRow || updatedRow === row) {
      return;
    }
    const changes: DropdownRowMutation[] = [];
    (Object.keys(updatedRow) as Array<keyof SampleSheetRowDraft>).forEach(key => {
      const previousValue = row[key];
      const nextValue = updatedRow[key];
      if (isEqual(previousValue ?? null, nextValue ?? null)) {
        return;
      }
      changes.push({ field: key, previousValue, newValue: nextValue });
    });
    Object.assign(row, updatedRow);
    if (changes.length > 0) {
      onRowMutation?.(row, changes);
    }
  };

  const getOptionsForRow = (row?: SampleSheetRowDraft): readonly string[] => {
    if (row && policy.optionsForRow) {
      const contextualOptions = policy.optionsForRow(row);
      if (contextualOptions.length > 0) {
        return contextualOptions;
      }
    }
    const baseOptions = policy.options();
    if (!row || !policy.formatOption) {
      return baseOptions;
    }
    const filtered = baseOptions.filter(option => {
      if (normalizeAndTrim(option) === emptyString) {
        return true;
      }
      const label = policy.formatOption?.(option, row);
      return label != null && label.trim().length > 0;
    });
    return filtered.length > 0 ? filtered : baseOptions;
  };

  const applyRawValue = (row: SampleSheetRowDraft, value: string | null) => {
    if (!policy.onRawValue) {
      return;
    }
    const updated = policy.onRawValue(row, value);
    applyRowMutationResult(row, updated);
  };

  const commitValue = (raw: string, row?: SampleSheetRowDraft) => {
    const normalized = normalizeAndTrim(raw);
    if (normalized === emptyString) {
      return {
        value: emptyString,
        canonical: null,
        options: getOptionsForRow(row),
      } as const;
    }
    const options = getOptionsForRow(row);
    const canonical = findCanonicalMatch(normalized, options, row);
    if (canonical) {
      return {
        value: canonical,
        canonical,
        options,
      } as const;
    }
    if (policy.snapMode === 'unique-substring') {
      const uniqueMatch = findUniqueSubstringMatch(normalized, options, candidate => getLabel(candidate, row), row);
      if (uniqueMatch) {
        const resolvedCanonical = findCanonicalMatch(uniqueMatch, options, row);
        return {
          value: uniqueMatch,
          canonical: resolvedCanonical,
          options,
        } as const;
      }
    }
    return {
      value: normalized,
      canonical: null,
      options,
    } as const;
  };

  const snapDropdownValue = (row: SampleSheetRowDraft, raw: string, options?: DropdownSnapOptions): string => {
    const rawString = raw;
    const handleRawOnlyValue = (nextValue: string): string => {
      if (nextValue === emptyString) {
        applyRawValue(row, null);
        return emptyString;
      }
      applyRawValue(row, nextValue);
      return nextValue;
    };

    if (options?.suppressCanonicalSelect) {
      const normalizedRaw = normalizeAndTrim(rawString);
      return handleRawOnlyValue(normalizedRaw);
    }

    const { value, canonical, options: choiceOptions } = commitValue(rawString, row);
    if (value === emptyString) {
      return handleRawOnlyValue(emptyString);
    }

    handleRawOnlyValue(value);

    if (!policy.onCanonicalSelect) {
      return value;
    }

    const canonicalForSelect = canonical ?? findCanonicalMatch(value, choiceOptions, row);

    if (canonicalForSelect) {
      const updatedRow = policy.onCanonicalSelect(row, canonicalForSelect);
      applyRowMutationResult(row, updatedRow);
    }

    return value;
  };

  const resolveCanonicalValue = (value: string, row?: SampleSheetRowDraft) => {
    const options = getOptionsForRow(row);
    return findCanonicalMatch(value, options, row);
  };

  return {
    getOptionsForRow,
    getLabel,
    commitValue,
    snapDropdownValue,
    resolveCanonicalValue,
  } satisfies DropdownRuntime;
}

export function createDropdownValueSnapper(
  policy: DropdownPolicy,
  onRowMutation?: DropdownRowMutationHandler,
): (
  row: SampleSheetRowDraft,
  raw: string,
  options?: DropdownSnapOptions,
) => string {
  const runtime = createDropdownRuntime(policy, onRowMutation);
  return (row, raw, opts) => runtime.snapDropdownValue(row, raw, opts);
}

function getSpecialCreateLabel(snapMode: SnapMode): (typed: string) => string {
  if (snapMode === 'exact-only') {
    return typed => `Create "${typed}"`;
  }
  return typed => `Use "${typed}" (invalid)`;
}

export function buildDropdownColDef<K extends keyof SampleSheetRowDraft>(
  field: K,
  header: string,
  policy: DropdownPolicy,
  shared: {
    getCellIssues: CellIssueLookup;
    isRowReadOnly: (row: SampleSheetRowDraft) => boolean;
    onDropdownRowMutation?: DropdownRowMutationHandler;
    isSamplingPointColumn?: boolean;
  },
): ColDef<SampleSheetRowDraft> {
  const { getOptionsForRow, getLabel, commitValue, snapDropdownValue, resolveCanonicalValue } = createDropdownRuntime(
    policy,
    shared.onDropdownRowMutation,
  );

  const dropdownColDef: ColDef<SampleSheetRowDraft> = {
    field,
    headerName: header,
    cellEditor: FreeTextDropdownEditor as never,
    cellEditorPopup: true,
    cellEditorPopupPosition: 'under',
    editable: (params: EditableCallbackParams<SampleSheetRowDraft>) => {
      const data = params.data;
      if (!data) {
        return false;
      }
      // Sampling point columns are not editable for submitted samples
      if (shared.isSamplingPointColumn && data.isSubmittedSample) {
        return false;
      }
      return !shared.isRowReadOnly(data);
    },
    valueParser: undefined,
    valueSetter: params => {
      const row = params.data;
      const rawNextValue: unknown = params.newValue;
      const rawString = typeof rawNextValue === 'string' ? rawNextValue : String(rawNextValue ?? emptyString);
      const originalValue = row[field];
      const snapped = snapDropdownValue(row, rawString);
      const finalValue = snapped === emptyString ? null : snapped;
      if (row[field] === originalValue) {
        row[field] = finalValue as SampleSheetRowDraft[K];
      }
      return true;
    },
    valueFormatter: (params: ValueFormatterParams<SampleSheetRowDraft>) => {
      const row = params.data;
      const rawValue: unknown = params.value;
      if (rawValue == null || rawValue === emptyString) {
        return policy.emptyLabel ?? emptyString;
      }
      if (typeof rawValue !== 'string') {
        return String(rawValue);
      }
      const canonical = resolveCanonicalValue(rawValue, row);
      if (canonical) {
        const formatted = getLabel(canonical, row);
        return formatted || canonical;
      }
      if (policy.formatOption) {
        const fallbackLabel = policy.formatOption(rawValue, row);
        if (fallbackLabel.trim().length > 0) {
          return fallbackLabel;
        }
      }
      return rawValue;
    },
    keyCreator: params => {
      const row = params.node?.data;
      const rawValue: unknown = params.value;
      if (rawValue == null || rawValue === emptyString) {
        return emptyString;
      }
      if (typeof rawValue !== 'string') {
        return String(rawValue);
      }
      const canonical = resolveCanonicalValue(rawValue, row);
      if (canonical) {
        const formatted = getLabel(canonical, row ?? undefined);
        return formatted || canonical;
      }
      if (policy.formatOption) {
        const fallbackLabel = policy.formatOption(rawValue, row ?? undefined);
        if (fallbackLabel.trim().length > 0) {
          return fallbackLabel;
        }
      }
      return rawValue;
    },
    cellEditorParams: (params: ICellEditorParams<SampleSheetRowDraft | undefined, unknown>) => {
      const row = params.data ?? undefined;
      const options = getOptionsForRow(row);
      const formatOption = policy.formatOption
        ? (value: string, currentRow?: SampleSheetRowDraft) => getLabel(value, currentRow ?? row)
        : undefined;
      const editorConfig: FreeTextDropdownEditorConfig = {
        options,
        formatOption,
        specialCreateLabel: getSpecialCreateLabel(policy.snapMode),
        onCommit: value => commitValue(value, row).value,
        resolveCanonicalValue: value => resolveCanonicalValue(value, row),
      };
      return editorConfig;
    },
  } satisfies ColDef<SampleSheetRowDraft>;

  return dropdownColDef;
}
